/**
 * Pseudo du joueur local — purement client (affichage HUD).
 *
 * Le `session_token` Lightning reste l'identité côté serveur : le pseudo n'est
 * jamais envoyé au worker ni au relay, c'est une surcouche d'affichage.
 * Persisté en localStorage sous la même convention que la session d'arène.
 */

export const NICK_KEY = 'lightning-arena.nickname';
export const NICK_MAX = 14;
export const NICK_DEFAULT = 'PLAYER';

/**
 * Whitelist stricte : ni emoji, ni HTML, ni caractères de contrôle. Les lettres
 * latines accentuées sont admises (jeu francophone : « RENÉ » doit rester lisible).
 */
const FORBIDDEN = /[^A-Za-z0-9À-ÖØ-öø-ÿ_\- ]+/g;

/**
 * Filtre de frappe : nettoie sans couper l'élan (l'espace final reste possible
 * pour taper « SATOSHI KID »). Ne renvoie jamais le fallback.
 */
export function filterNicknameInput(raw: string): string {
  return raw.replace(FORBIDDEN, '').replace(/\s{2,}/g, ' ').replace(/^\s+/, '').slice(0, NICK_MAX);
}

/** Valeur définitive utilisée en jeu : vide ou invalide → NICK_DEFAULT. */
export function sanitizeNickname(raw: string | null | undefined): string {
  if (!raw) return NICK_DEFAULT;
  return filterNicknameInput(raw).trim() || NICK_DEFAULT;
}

/** Pseudo mémorisé (déjà assaini : le localStorage est modifiable à la main). */
export function loadNickname(): string {
  try { return sanitizeNickname(localStorage.getItem(NICK_KEY)); } catch { return NICK_DEFAULT; }
}

export function saveNickname(name: string): string {
  const clean = sanitizeNickname(name);
  try { localStorage.setItem(NICK_KEY, clean); } catch { /* mode privé : le pseudo reste valable pour la session */ }
  return clean;
}
